import { useRef } from "react"

import { HelpCenterContent, HelpCenterHeader, WikiSection, DownloadCard } from "@/areas/help-center"
import Article from "@/shared/layouts/Article/Article"

function LinkingTableNeedsSetup() {
  const contentRef = useRef<HTMLDivElement>(null)

  return (
    <HelpCenterContent ref={contentRef}>
      <HelpCenterHeader
        title="OCB Desktop App Setup"
        subtitle="What you need to configure inside TABLE NEEDS POS so it can send orders correctly."
        logo="/static/wiki/table-needs-logo.png"
        backPath="/help-center/linking-pos-devices"
        backLabel="Linking POS Devices"
        contentRef={contentRef}
      />

      <WikiSection id="overview" title="OVERVIEW">
        <Article>
          <p>The following process is going to explain step by:</p>
          <ul className="wiki-list">
            <li>Install the correct Table Needs application version;</li>
            <li>Launch and configure the OCB app linking it to Table Needs POS via unique identifier;</li>
            <li>Connecting and Configuring the POS inside Table Needs Backend;</li>
            <li>Retrieving the necessary Screen ID to configure a dedicated Table Needs station linked with OCB App.</li>
          </ul>

          <h3>Requirements</h3>
          <p>To successfully complete this setup, you will need:</p>
          <ul className="wiki-list">
            <li>Administrator access to Table Needs POS system.</li>
          </ul>
        </Article>
      </WikiSection>

      <WikiSection id="download-install-ocb" title="A - DOWNLOAD AND INSTALL THE OCB DESKTOP APP">
        <Article>
          <p>In our Download Hub, you'll find a variety of essential downloads. After downloading the OCB app, extract the files, run the .exe installer, and complete the installation steps.</p>

          <DownloadCard
            name="OCB App"
            version="v.0.41.2"
            releaseDate="Month, DD, YYYY"
            downloadUrl="#"
          />

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After downloading, extract the files, run the <strong>.exe installer</strong>, and complete the installation steps.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Once installed, locate the OCB app on your computer and launch it.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>The application will automatically start loading and preparing for use.</p>
          </div>

          <p>See below for a visual representation of the OCB App loading screen.</p>
        </Article>
      </WikiSection>

      <WikiSection id="open-access-configuration" title="B — OPEN THE OCB APP AND ACCESS THE CONFIGURATION FLOW">
        <Article>
          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Open the <strong>OCB App</strong> on your desktop.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>You will be presented with the main screen. From here, you can access the application settings and configuration panel.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>If this is your first time using the app, you may be prompted to configure your screen ID.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Back to the main screen, click on the settings icon or open the configuration panel to begin the setup process.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/linking-oracle-01.png" alt="OCB App welcome screen" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Click on the <strong>Settings</strong> icon.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>The settings panel will display on the left with various options such as <strong>Text Settings</strong>, <strong>Background Settings</strong>, and <strong>Other Settings</strong>.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/linking-oracle-02.png" alt="OCB settings panel" className="wiki-screenshot" />
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Navigate to the <strong>password</strong> field.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Enter the password provided by the Stream team to proceed.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Once authenticated, you&apos;ll be able to access the configuration wizard to link your Table Needs POS.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/linking-oracle-03.png" alt="Password entry screen" className="wiki-screenshot" />
          </div>
        </Article>
      </WikiSection>

      <WikiSection id="configure-ocb-table-needs" title="C - CONFIGURE OCB APP FOR TABLE NEEDS POS">
        <Article>
          <h3>Select Table Needs Integration</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After entering your password, you will be directed to the integration wizard.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>On the first step, you&apos;ll see a screen titled <strong>CHOOSE POINT OF SALES</strong>. This is where you select which POS system you want to integrate with.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Click on the dropdown menu and select <strong>Table Needs Integration</strong> from the list of available POS systems.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/oracle/linking-oracle-04.png" alt="Choose Point of Sales screen" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/table-needs/linking-table-needs-05.png" alt="POS dropdown menu with Table Needs" className="wiki-screenshot" />
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/table-needs/linking-table-needs-06.png" alt="Table Needs Integration selected" className="wiki-screenshot" />
          </div>

          <h3>Define Integration Parameters</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After selecting Table Needs Integration, click the <strong>Next</strong> button to proceed to the configuration form.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>On step 2, you will be prompted to <strong>ENTER YOUR TABLE NEEDS IDENTIFIER</strong>. This unique identifier links the OCB App to your Table Needs POS system installation.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>You&apos;ll also see an option to <strong>ENABLE SOUNDHOUND</strong>. Toggle this switch if you want to enable voice ordering capabilities through SoundHound integration.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>After filling in your Table Needs identifier, click the <strong>Next</strong> button to proceed.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>You will be redirected to the confirmation screen.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/table-needs/linking-table-needs-07.png" alt="Enter Table Needs identifier" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/table-needs/linking-table-needs-08.png" alt="Confirm your choices" className="wiki-screenshot" />
          </div>

          <h3>Validate OCB App Status</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>On the final step, you will see a <strong>CONFIRM YOUR CHOICES</strong> screen that displays all the configuration details you&apos;ve entered.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Review all the information carefully to ensure everything is correct. Check the integration name and your Table Needs identifier.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Once you&apos;ve verified all details, click the <strong>Confirm</strong> button to complete the setup.</p>
          </div>

          <div className="wiki-image-single">
            <img src="/static/wiki/oracle/linking-oracle-09.png" alt="Setup complete - Welcome screen" className="wiki-screenshot" />
          </div>
        </Article>
      </WikiSection>

      <WikiSection id="retrieve-screen-id" title="D - RETRIEVE SCREEN ID FOR TABLE-NEEDS SETUP">
        <Article>
          <p>To link the OCB App with a specific Table Needs station, you&apos;ll need to retrieve the Screen ID from the OCB App and configure it in the Table Needs backend.</p>

          <h3>Retrieve the App ID from the OCB App</h3>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Open the <strong>OCB App</strong> on your Windows workstation. Once loaded, navigate to the app&apos;s settings by clicking the gear icon or accessing the <strong>Settings</strong> menu.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>In the Settings panel, locate the <strong>Screen ID</strong> field.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>Copy the Screen ID displayed (e.g., <strong>a1b2c3d4e5f6</strong>). This unique identifier links the OCB App instance to a specific Table Needs station.</p>
          </div>

          <div className="wiki-step">
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-arrow" />
            <p>You can click the <strong>copy icon</strong> next to the Screen ID to automatically copy it to your clipboard. Keep this ID handy, as you&apos;ll need to enter it in the Table Needs backend in the next step.</p>
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/table-needs/linking-table-needs-09.png" alt="OCB App welcome screen showing settings access" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/table-needs/linking-table-needs-10.png" alt="Settings panel with Screen ID" className="wiki-screenshot" />
          </div>

          <div className="wiki-image-flow">
            <img src="/static/wiki/table-needs/linking-table-needs-11.png" alt="Copy Screen ID" className="wiki-screenshot" />
            <img src="/static/wiki/arrow.svg" alt="" className="wiki-flow-arrow" />
            <img src="/static/wiki/table-needs/linking-table-needs-12.png" alt="Screen ID copied confirmation" className="wiki-screenshot" />
          </div>
        </Article>
      </WikiSection>
    </HelpCenterContent>
  )
}

export default LinkingTableNeedsSetup
